import { useState } from "react";
import { X, RotateCw, Maximize2, Sun, Move, Camera, Download } from "lucide-react";

/**
 * BachePreview3D — Mockup mural interactif avec CSS 3D transforms.
 * L'utilisateur voit la bâche installée sur les vraies photos du siège
 * d'Atchrimi-Komé avant de commander l'impression chez Kpogan.
 *
 * Aucune librairie 3D (Three.js) nécessaire : perspective + rotate + shadow
 * suffisent pour un rendu réaliste "à plat sur mur".
 */

const WALL_PHOTOS = [
  { id: "facade-verte", label: "🏠 Façade fenêtres vertes", src: "/images/siege-ag/facade-fenetres-vertes.jpg" },
  { id: "enseigne", label: "🪧 Enseigne officielle", src: "/images/siege-ag/enseigne-officielle.jpg" },
  { id: "siege-1", label: "🏗️ Siège vue 1", src: "/images/siege-ag/siege-ag-1.jpg" },
  { id: "siege-2", label: "🏗️ Siège vue 2", src: "/images/siege-ag/siege-ag-2.jpg" },
  { id: "siege-3", label: "🏗️ Siège vue 3", src: "/images/siege-ag/siege-ag-3.jpg" },
  { id: "porte-verte", label: "🚪 Porte verte", src: "/images/siege-ag/travaux-porte-verte.jpg" },
];

const BACHE_ASSETS = [
  { id: "bache-siege", label: "🎪 Bâche 3m × 1m Siège", src: "/docs/SAFUAYE_Bache_Siege_Atchrimi_Kome_300x100.png", ratio: 3 },
  { id: "bache-odon", label: "🎪 Bâche Odôn-Itsou A0", src: "/docs/SAFUAYE_OdonItsou_Bache_A0.png", ratio: 2 },
];

export default function BachePreview3D({ open, onClose }) {
  const [wall, setWall] = useState(WALL_PHOTOS[0]);
  const [asset, setAsset] = useState(BACHE_ASSETS[0]);
  const [scale, setScale] = useState(60);      // % of container width
  const [posX, setPosX] = useState(50);        // % horizontal
  const [posY, setPosY] = useState(40);        // % vertical
  const [rotY, setRotY] = useState(-8);        // horizontal tilt (perspective)
  const [rotX, setRotX] = useState(2);         // vertical tilt
  const [brightness, setBrightness] = useState(95);
  const [shadow, setShadow] = useState(35);

  if (!open) return null;

  const resetAll = () => {
    setScale(60); setPosX(50); setPosY(40);
    setRotY(-8); setRotX(2); setBrightness(95); setShadow(35);
  };

  // Compute width from scale × wall photo aspect. Height derived from ratio.
  const bacheWidth = `${scale}%`;
  const bacheStyle = {
    position: "absolute",
    left: `${posX}%`,
    top: `${posY}%`,
    width: bacheWidth,
    aspectRatio: `${asset.ratio}`,
    transform: `translate(-50%, -50%) perspective(1200px) rotateY(${rotY}deg) rotateX(${rotX}deg)`,
    transformOrigin: "center center",
    filter: `brightness(${brightness}%) contrast(${brightness < 95 ? 105 : 100}%)`,
    boxShadow: `${rotY < 0 ? "-" : ""}${Math.abs(rotY) * 0.8}px 12px ${shadow}px rgba(0,0,0,${shadow / 120})`,
    transition: "transform 0.2s ease-out, filter 0.2s, box-shadow 0.2s",
    pointerEvents: "none",
    userSelect: "none",
  };

  return (
    <div className="fixed inset-0 bg-black/85 z-[60] flex items-center justify-center p-4" data-testid="bache-preview-3d-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-[95vw] max-w-6xl max-h-[95vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0B2545] to-[#08192F] text-white p-4 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Camera className="w-5 h-5 text-amber-400" />
              Aperçu mural 3D — Bâche sur le siège d&apos;Atchrimi-Komé
            </h3>
            <p className="text-xs text-amber-200 italic">
              Visualisez la bâche installée avant impression chez Kpogan (Atakpamé)
            </p>
          </div>
          <button
            onClick={onClose}
            data-testid="bache-preview-close"
            className="p-2 hover:bg-white/10 rounded-lg transition"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left: 3D stage */}
          <div className="flex-1 relative bg-slate-900 overflow-hidden flex items-center justify-center min-h-[380px]">
            <div className="relative w-full h-full" style={{ perspective: "1400px" }}>
              {/* Wall photo */}
              <img
                src={wall.src}
                alt={wall.label}
                className="w-full h-full object-cover"
                style={{ filter: "brightness(90%)" }}
                data-testid="bache-preview-wall"
              />
              {/* Bâche overlay */}
              <img
                src={asset.src}
                alt="Bâche SAFUAYE"
                style={bacheStyle}
                data-testid="bache-preview-overlay"
                draggable="false"
              />
              {/* 4 nail markers (small circles at corners) */}
              {[
                { top: `calc(${posY}% - ${scale / (asset.ratio * 2)}% - 3px)`, left: `calc(${posX}% - ${scale / 2}% - 3px)` },
                { top: `calc(${posY}% - ${scale / (asset.ratio * 2)}% - 3px)`, left: `calc(${posX}% + ${scale / 2}% - 3px)` },
                { top: `calc(${posY}% + ${scale / (asset.ratio * 2)}% - 3px)`, left: `calc(${posX}% - ${scale / 2}% - 3px)` },
                { top: `calc(${posY}% + ${scale / (asset.ratio * 2)}% - 3px)`, left: `calc(${posX}% + ${scale / 2}% - 3px)` },
              ].map((p, i) => (
                <span
                  key={i}
                  className="absolute w-2 h-2 rounded-full bg-slate-800 border border-slate-400 shadow"
                  style={{ ...p, pointerEvents: "none" }}
                />
              ))}
              {/* Bottom-left dimension hint */}
              <div className="absolute bottom-2 left-2 bg-black/70 text-white text-[11px] px-2 py-1 rounded backdrop-blur">
                📐 Dimensions réelles : {asset.ratio === 3 ? "300 × 100 cm" : "≈ 200 × 100 cm"} · Ratio {asset.ratio}:1
              </div>
              {/* Top-right label */}
              <div className="absolute top-2 right-2 bg-amber-400/90 text-slate-900 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded">
                Aperçu simulé
              </div>
            </div>
          </div>

          {/* Right: controls */}
          <div className="md:w-[320px] w-full p-4 bg-slate-50 overflow-y-auto space-y-4 border-l">
            {/* Choose wall */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-700 flex items-center gap-1">
                🏠 Choisir le mur
              </label>
              <select
                value={wall.id}
                onChange={(e) => setWall(WALL_PHOTOS.find(w => w.id === e.target.value))}
                className="mt-1 w-full px-3 py-2 border rounded text-sm"
                data-testid="bache-preview-wall-select"
              >
                {WALL_PHOTOS.map(w => (
                  <option key={w.id} value={w.id}>{w.label}</option>
                ))}
              </select>
            </div>

            {/* Choose bâche */}
            <div>
              <label className="text-xs font-bold uppercase text-slate-700 flex items-center gap-1">
                🎪 Choisir la bâche
              </label>
              <select
                value={asset.id}
                onChange={(e) => setAsset(BACHE_ASSETS.find(a => a.id === e.target.value))}
                className="mt-1 w-full px-3 py-2 border rounded text-sm"
                data-testid="bache-preview-asset-select"
              >
                {BACHE_ASSETS.map(a => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </div>

            <hr className="border-slate-200" />

            {/* Size */}
            <Slider
              label={<><Maximize2 className="inline w-3 h-3" /> Taille apparente</>}
              value={scale} min={20} max={90} unit="%" onChange={setScale}
              testId="bache-preview-scale"
            />
            {/* Horizontal position */}
            <Slider
              label={<><Move className="inline w-3 h-3" /> Position horizontale</>}
              value={posX} min={10} max={90} unit="%" onChange={setPosX}
              testId="bache-preview-posx"
            />
            {/* Vertical position */}
            <Slider
              label={<><Move className="inline w-3 h-3 rotate-90" /> Position verticale</>}
              value={posY} min={10} max={90} unit="%" onChange={setPosY}
              testId="bache-preview-posy"
            />
            {/* Y rotation */}
            <Slider
              label={<><RotateCw className="inline w-3 h-3" /> Inclinaison horizontale</>}
              value={rotY} min={-25} max={25} unit="°" onChange={setRotY}
              testId="bache-preview-roty"
            />
            {/* X rotation */}
            <Slider
              label={<><RotateCw className="inline w-3 h-3 -rotate-90" /> Inclinaison verticale</>}
              value={rotX} min={-15} max={15} unit="°" onChange={setRotX}
              testId="bache-preview-rotx"
            />
            {/* Brightness */}
            <Slider
              label={<><Sun className="inline w-3 h-3" /> Luminosité</>}
              value={brightness} min={60} max={120} unit="%" onChange={setBrightness}
              testId="bache-preview-brightness"
            />
            {/* Shadow */}
            <Slider
              label="🌑 Ombre portée"
              value={shadow} min={0} max={80} unit="px" onChange={setShadow}
              testId="bache-preview-shadow"
            />

            <hr className="border-slate-200" />

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={resetAll}
                data-testid="bache-preview-reset"
                className="px-3 py-2 text-xs bg-slate-200 hover:bg-slate-300 rounded font-medium"
              >
                ↺ Réinitialiser
              </button>
              <a
                href={asset.src}
                download
                data-testid="bache-preview-download"
                className="inline-flex items-center justify-center gap-1 px-3 py-2 text-xs bg-amber-600 hover:bg-amber-700 text-white rounded font-medium"
              >
                <Download className="w-3 h-3" /> Télécharger
              </a>
            </div>

            <div className="bg-fuchsia-50 border-l-4 border-fuchsia-400 p-3 rounded text-[11px] text-fuchsia-900">
              💡 <strong>Astuce :</strong> Utilisez la vue « Façade fenêtres vertes » pour un rendu réaliste au-dessus de la porte principale. Ajustez la taille à ~60% pour respecter les proportions réelles 3m×1m.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Small slider primitive with label + numeric value */
function Slider({ label, value, min, max, unit, onChange, testId }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-700">{label}</label>
        <span className="text-xs font-mono text-slate-500">{value}{unit}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        className="w-full accent-fuchsia-600"
      />
    </div>
  );
}
